import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";
import { UserPlus, Mail, Trash2, Sparkles, AlertCircle, ArrowRight, Send, X, CheckCircle2 } from "lucide-react";

export default function Roster() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/agency/roster").then(({ data }) => setRoster(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this athlete from your roster? The athlete keeps their profile and history — only the management link is removed.")) return;
    try { await api.delete(`/agency/roster/${id}`); toast.success("Removed from roster"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendInvite = async (id) => {
    try {
      const { data } = await api.post("/agency/roster/claim-invite", { athlete_user_id: id });
      if (data.stub) toast("Email sender not configured (dev). Token logged server-side.", { duration: 5000 });
      else toast.success("Claim invite email sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const actAs = (athleteId) => {
    // Set acting-as in localStorage; the AuthContext + api wrapper read this to attach X-Acting-As header.
    localStorage.setItem("acting_as_id", athleteId);
    const ath = roster.find(a => a.athlete_user_id === athleteId);
    localStorage.setItem("acting_as_name", ath?.full_name || "");
    toast.success(`Acting as ${ath?.full_name}`);
    navigate("/app/profile");
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="label-eyebrow">{user?.account_type === "club" ? "Academy roster" : "Agency roster"}</div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="roster-title">Your Athletes</h1>
            <p className="text-ink-muted mt-2 text-sm">Build profiles, run outreach, manage partnerships — on behalf of each athlete in your roster.</p>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-volt inline-flex items-center gap-2" data-testid="add-athlete-btn">
            <UserPlus className="w-4 h-4" /> Add Athlete
          </button>
        </div>

        {loading ? (
          <div className="surface p-12 text-center text-ink-muted">Loading…</div>
        ) : roster.length === 0 ? (
          <div className="surface p-12 text-center" data-testid="roster-empty">
            <Sparkles className="w-8 h-8 text-volt mx-auto mb-4" />
            <div className="font-heading text-2xl uppercase">Build your first profile</div>
            <p className="text-ink-muted text-sm mt-2 mb-6 max-w-md mx-auto">
              Add an athlete now — even before they join. Their profile becomes a shared workspace once they claim it.
            </p>
            <button onClick={() => setShowAdd(true)} className="btn-volt inline-flex items-center gap-2 text-sm" data-testid="add-first-athlete-btn">
              <UserPlus className="w-4 h-4" /> Add your first athlete
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="roster-grid">
            {roster.map(a => (
              <div key={a.athlete_user_id} className="surface p-5 group" data-testid={`roster-item-${a.athlete_user_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg uppercase tracking-tight truncate">{a.full_name}</div>
                    <div className="text-xs text-ink-muted mt-1">{a.sport}{a.level && ` · ${a.level}`}</div>
                    {a.location && <div className="text-xs text-ink-muted mt-1">{a.location}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {a.positioning_score !== null && a.positioning_score !== undefined && (
                      <div className="text-volt font-mono text-2xl leading-none">{a.positioning_score}</div>
                    )}
                    <ClaimBadge claimed={a.claimed} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                  <Stat label="Open deals" value={a.open_deals} testid={`stat-deals-${a.athlete_user_id}`} />
                  <Stat label="Overdue" value={a.overdue_followups} alert={a.overdue_followups > 0} testid={`stat-overdue-${a.athlete_user_id}`} />
                </div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <button onClick={() => actAs(a.athlete_user_id)}
                    className="btn-volt text-xs inline-flex items-center justify-center gap-1.5"
                    data-testid={`act-as-${a.athlete_user_id}`}>
                    <Sparkles className="w-3.5 h-3.5" /> Open workspace
                  </button>
                  {a.claimed ? (
                    <span className="text-[10px] uppercase tracking-widest text-[#34C759] inline-flex items-center justify-center gap-1 border border-[#34C759]/40 py-2">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Claimed
                    </span>
                  ) : (
                    <button onClick={() => sendInvite(a.athlete_user_id)}
                      className="btn-ghost-line text-xs inline-flex items-center justify-center gap-1.5"
                      data-testid={`send-invite-${a.athlete_user_id}`}>
                      <Mail className="w-3.5 h-3.5" /> Invite to claim
                    </button>
                  )}
                </div>
                <div className="flex justify-end mt-3">
                  <button onClick={() => remove(a.athlete_user_id)} data-testid={`roster-remove-${a.athlete_user_id}`}
                    className="text-[10px] text-ink-muted hover:text-[#FF3B30] uppercase tracking-widest inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAdd && <AddAthleteModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
      </div>
    </DashboardLayout>
  );
}

function ClaimBadge({ claimed }) {
  if (claimed) return (
    <span className="text-[9px] uppercase tracking-widest border border-[#34C759]/40 text-[#34C759] px-2 py-0.5">Claimed</span>
  );
  return (
    <span className="text-[9px] uppercase tracking-widest border border-volt/40 text-volt px-2 py-0.5">Unclaimed</span>
  );
}
function Stat({ label, value, alert = false, testid }) {
  return (
    <div className="border-l-2 pl-3 py-1" style={{ borderColor: alert ? "#FF3B30" : "var(--line)" }} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-heading text-xl ${alert ? "text-[#FF3B30]" : ""}`}>{value}</div>
    </div>
  );
}

function AddAthleteModal({ onClose, onAdded }) {
  const [form, setForm] = useState({
    full_name: "", email: "", sport: "", level: "", location: "", club: "",
    instagram_url: "", tiktok_url: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Full name is required");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.email) delete body.email;
      await api.post("/agency/roster", body);
      toast.success(`${form.full_name} added to roster`);
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add athlete"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="surface w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-athlete-modal">
        <div className="p-6 border-b border-line flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Roster</div>
            <h2 className="font-heading text-3xl uppercase tracking-tighter mt-2">Add Athlete</h2>
            <p className="text-xs text-ink-muted mt-2">Creates the athlete's PITCHD profile instantly. You can edit it, run outreach, and invite them to claim ownership at any time.</p>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Full name *"><Input value={form.full_name} onChange={v => upd("full_name", v)} testid="add-name" /></Field>
            <Field label="Email (optional for invite later)"><Input value={form.email} onChange={v => upd("email", v)} placeholder="athlete@example.com" testid="add-email" /></Field>
            <Field label="Sport"><Input value={form.sport} onChange={v => upd("sport", v)} placeholder="Football" testid="add-sport" /></Field>
            <Field label="Level"><Input value={form.level} onChange={v => upd("level", v)} placeholder="Semi-pro" testid="add-level" /></Field>
            <Field label="Location"><Input value={form.location} onChange={v => upd("location", v)} placeholder="London, UK" testid="add-location" /></Field>
            <Field label="Club / team"><Input value={form.club} onChange={v => upd("club", v)} testid="add-club" /></Field>
            <Field label="Instagram URL"><Input value={form.instagram_url} onChange={v => upd("instagram_url", v)} placeholder="https://instagram.com/@…" testid="add-instagram" /></Field>
            <Field label="TikTok URL"><Input value={form.tiktok_url} onChange={v => upd("tiktok_url", v)} placeholder="https://tiktok.com/@…" testid="add-tiktok" /></Field>
          </div>
          <Field label="Internal notes (private to your agency)">
            <textarea rows={3} value={form.notes} onChange={e => upd("notes", e.target.value)} maxLength={400} data-testid="add-notes"
              className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white resize-none" />
          </Field>
          <div className="surface bg-bg-elev p-4 text-xs text-ink-muted">
            <AlertCircle className="w-4 h-4 inline mr-2 text-volt" />
            Athlete identity is portable: if you ever stop managing this athlete, they keep their profile, media kit, deals, and conversation history.
          </div>
        </div>
        <div className="p-6 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost-line text-sm" data-testid="add-cancel-btn">Cancel</button>
          <button onClick={submit} disabled={saving || !form.full_name.trim()}
            className="btn-volt text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="add-submit-btn">
            <Send className="w-4 h-4" /> {saving ? "Creating…" : "Add to roster"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>; }
function Input({ value, onChange, placeholder = "", testid }) {
  return <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
    className="w-full bg-bg border border-line focus:border-volt outline-none px-3 py-2.5 text-white text-sm" />;
}
